import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { siteApi } from "@/lib/api";
import { getStoredAuthToken } from "@/lib/authStorage";
import {
  notificationCounts,
  notificationPresentation,
  NOTIFICATION_REFRESH_MS,
  NOTIFICATION_REMINDER_MS,
  NOTIFICATION_SYNC_EVENTS,
} from "../features/memberNotifications/policy/notificationReminder.policy";

export { NOTIFICATION_REFRESH_MS, NOTIFICATION_REMINDER_MS };

// Dismiss is not a read: reminders consume successful counts without API calls.
const useMemberNotifications = (enabled, options = {}) => {
  const accountKey = options.accountKey || "member";
  const suppressedTypes = options.suppressedTypes || {};
  const [counts, setCounts] = useState({});
  const [alerts, setAlerts] = useState([]);
  const runtimeRef = useRef({});
  runtimeRef.current = { enabled, accountKey, suppressedTypes };
  const generationRef = useRef(0);
  const requestRef = useRef(null);
  const countsRef = useRef({ note: 0, inquiry: 0 });
  const shownAtRef = useRef({ note: 0, inquiry: 0 });

  const pushAlert = useCallback((type, count) => {
    if (!count || runtimeRef.current.suppressedTypes[type]) return;
    shownAtRef.current[type] = Date.now();
    setAlerts((previous) => {
      const existing = previous.find((item) => item.type === type);
      const next = {
        id: existing?.id || `${type}-${Date.now()}`,
        type, count, ...notificationPresentation(type),
      };
      return existing ? previous.map((item) => item.type === type ? next : item) : [...previous, next];
    });
  }, []);

  const removeAlertByType = useCallback((type) => {
    setAlerts((previous) => previous.some((item) => item.type === type)
      ? previous.filter((item) => item.type !== type) : previous);
  }, []);

  const dismissAlert = useCallback((id) => {
    setAlerts((previous) => {
      const dismissed = previous.find((item) => item.id === id);
      if (dismissed) shownAtRef.current[dismissed.type] = Date.now();
      return previous.filter((item) => item.id !== id);
    });
  }, []);

  useEffect(() => {
    if (suppressedTypes.note) removeAlertByType("note");
    if (suppressedTypes.inquiry) removeAlertByType("inquiry");
  }, [removeAlertByType, suppressedTypes.inquiry, suppressedTypes.note]);

  useEffect(() => {
    const generation = ++generationRef.current;
    countsRef.current = { note: 0, inquiry: 0 };
    shownAtRef.current = { note: 0, inquiry: 0 };
    setCounts({});
    setAlerts([]);
    if (!enabled) return undefined;

    const current = () => generationRef.current === generation &&
      runtimeRef.current.enabled && runtimeRef.current.accountKey === accountKey;
    const loadCounts = async () => {
      if (!current() || document.visibilityState === "hidden") return;
      if (requestRef.current?.generation === generation) return;
      const request = { generation, token: getStoredAuthToken() };
      requestRef.current = request;
      try {
        const response = await siteApi.getMemberNotificationCount();
        if (!current() || request.token !== getStoredAuthToken()) return;
        if (response?.success !== true || !response.data) return;
        const nextCounts = notificationCounts(response.data);
        const previousCounts = countsRef.current;
        countsRef.current = nextCounts;
        setCounts(response.data);
        for (const type of ["note", "inquiry"]) {
          if (!nextCounts[type] || runtimeRef.current.suppressedTypes[type]) {
            removeAlertByType(type);
          } else if (nextCounts[type] > previousCounts[type]) {
            pushAlert(type, nextCounts[type]);
          } else {
            setAlerts((previous) => previous.map((item) => item.type === type
              ? { ...item, count: nextCounts[type] } : item));
          }
        }
      } catch {
        // Preserve successful counts; retry only on the existing poll.
      } finally {
        if (requestRef.current === request) requestRef.current = null;
      }
    };
    void loadCounts();
    const interval = window.setInterval(loadCounts, NOTIFICATION_REFRESH_MS);
    NOTIFICATION_SYNC_EVENTS.forEach((name) => window.addEventListener(name, loadCounts));
    window.addEventListener("focus", loadCounts);
    document.addEventListener("visibilitychange", loadCounts);
    return () => {
      generationRef.current += 1;
      window.clearInterval(interval);
      NOTIFICATION_SYNC_EVENTS.forEach((name) => window.removeEventListener(name, loadCounts));
      window.removeEventListener("focus", loadCounts);
      document.removeEventListener("visibilitychange", loadCounts);
    };
  }, [accountKey, enabled, pushAlert, removeAlertByType]);

  useEffect(() => {
    if (!enabled) return undefined;
    const deadlines = ["note", "inquiry"].filter((type) => countsRef.current[type] > 0 &&
      !runtimeRef.current.suppressedTypes[type] && !alerts.some((item) => item.type === type));
    if (!deadlines.length) return undefined;
    const due = Math.min(...deadlines.map((type) => shownAtRef.current[type] + NOTIFICATION_REMINDER_MS));
    const remind = () => {
      if (document.visibilityState === "hidden") return;
      for (const type of deadlines) {
        if (Date.now() >= shownAtRef.current[type] + NOTIFICATION_REMINDER_MS) {
          pushAlert(type, countsRef.current[type]);
        }
      }
    };
    const timer = window.setTimeout(remind, Math.max(0, due - Date.now()));
    document.addEventListener("visibilitychange", remind);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", remind);
    };
  }, [enabled, accountKey, counts, alerts, suppressedTypes.note, suppressedTypes.inquiry, pushAlert]);

  return useMemo(() => {
    const unread = notificationCounts(counts);
    return { counts, alerts, dismissAlert, unreadCount: unread.note + unread.inquiry,
      noteUnreadCount: unread.note, inquiryUnreadCount: unread.inquiry };
  }, [alerts, counts, dismissAlert]);
};

export default useMemberNotifications;
